import { UpOutlined } from '@ant-design/icons';
import cx from 'classnames';
import AnimatedHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { useToggleState } from '@app/utils';
import { Namespace } from '@data';
import { AntdInput, AntdTypography, Button } from '@ui/components';
import { Filter as FilterType } from '../../../../types';
import { OptionsListLoader, OptionsListPreview } from './components';
import { OptionsList } from './components/OptionsList';

const { Text } = AntdTypography;

type FilterProps = {
  filter: FilterType;
  isFirst: boolean;
};

// Filter block component (Answer Type, Generation Type, ICD Codes...)
export const Filter = ({
  isFirst,
  filter: {
    key,
    title,
    options,
    isDynamic,
    isLoading,
    searchValue,
    placeholder,
    openByDefault,
    shouldSearch,
    debouncedSearchValue,
    onChange,
    onSearch,
    getIsChecked,
  },
}: FilterProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const [isFilterOpen, toggleFilterOpen] = useToggleState(openByDefault);

  return (
    <li
      style={!isFirst ? { borderTopStyle: 'solid' } : {}}
      className={cx('py-2', !isFirst && 'border-t border-t-base-200')}
    >
      <Button
        type="text"
        className="w-full justify-start px-1 py-2"
        onClick={toggleFilterOpen}
        data-testid={`filter-block-${key}`}
      >
        <Text className="font-semibold text-base line-clamp-1">{t(title)}</Text>
        <UpOutlined
          className={cx('ml-auto duration-300', isFilterOpen ? 'rotate-0' : 'rotate-180')}
        />
      </Button>
      {isLoading ? (
        <OptionsListLoader />
      ) : (
        <AnimatedHeight height={isFilterOpen ? 'auto' : 0}>
          {onSearch && (
            <AntdInput
              placeholder={t(placeholder ?? 'Search')}
              value={searchValue}
              onChange={(e) => onSearch(e.target.value)}
              className="w-full my-2"
            />
          )}
          {/* top level options render */}
          <ul className="list-none p-0 mt-1">
            {options.map((option) =>
              debouncedSearchValue && shouldSearch ? (
                <OptionsListPreview
                  key={option.value}
                  option={option}
                  isDynamic={isDynamic}
                  onChange={onChange}
                  getIsChecked={getIsChecked}
                />
              ) : (
                <OptionsList
                  key={option.value}
                  option={option}
                  isDynamic={isDynamic}
                  onChange={onChange}
                  getIsChecked={getIsChecked}
                />
              ),
            )}
          </ul>
        </AnimatedHeight>
      )}
    </li>
  );
};
